// The Mk 8 rangekeeper's graphic time-range plotter, drawn live to a canvas:
// observed radar ranges as phosphor dots, the generated (dead-reckoned) range
// as a continuous pen line, salvo and splash marks, plus the rate-matching
// instruments on the right — estimated target course/speed dials and the
// generated-vs-observed range-rate and bearing-drift meters. The same canvas
// is textured onto the plot-room bulkhead in 3D and blown up in the station
// UI, exactly like the CIC PPI scope.

import * as THREE from "three";
import { KTS } from "./const.js";

const W = 640, H = 340;
const PLOT_W = 425;      // left: time-range plot
const INK = "#cfe0d4", DIM = "#527257", GRID = "#1d3a28", PAPER = "#0b1a10";
const GOLD = "#ffcf5c", OBS = "#8fe3a0", BAD = "#ff6a5e";

export class PlotBoard {
  constructor(G) {
    this.G = G;
    this.canvas = document.createElement("canvas");
    this.canvas.width = W; this.canvas.height = H;
    this.ctx = this.canvas.getContext("2d");
    this.tex = new THREE.CanvasTexture(this.canvas);
    this._acc = 0;
    this.draw();
  }

  update(dt) {
    this._acc += dt;
    if (this._acc > 0.2) { this._acc = 0; this.draw(); }
  }

  draw() {
    const ctx = this.ctx, fc = this.G.fc, s = this.G.shipState;
    ctx.fillStyle = PAPER; ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "#2c4636"; ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, W - 2, H - 2);
    ctx.beginPath(); ctx.moveTo(PLOT_W, 0); ctx.lineTo(PLOT_W, H); ctx.stroke();

    this.drawTimeRange(ctx, fc);
    this.drawInstruments(ctx, fc, s);
    this.tex.needsUpdate = true;
  }

  drawTimeRange(ctx, fc) {
    const x0 = 14, y0 = 30, w = PLOT_W - 28, h = H - 62;
    ctx.font = "11px monospace"; ctx.textAlign = "left"; ctx.fillStyle = DIM;
    ctx.fillText("TIME–RANGE PLOT", x0, 18);
    if (!fc.target) {
      ctx.fillStyle = DIM; ctx.textAlign = "center"; ctx.font = "13px monospace";
      ctx.fillText("NO TARGET DESIGNATED", PLOT_W / 2, H / 2 - 8);
      ctx.fillText("CIC HOLDS THE RADAR PICTURE", PLOT_W / 2, H / 2 + 12);
      return;
    }
    const SPAN_T = 120; // seconds across the paper
    const tMax = fc.boardT, tMin = tMax - SPAN_T;
    // range window: follow the data
    let rMin = Infinity, rMax = -Infinity;
    for (const p of fc.history) {
      if (p.t < tMin) continue;
      if (p.obs !== undefined) { rMin = Math.min(rMin, p.obs); rMax = Math.max(rMax, p.obs); }
      rMin = Math.min(rMin, p.gen); rMax = Math.max(rMax, p.gen);
    }
    if (!Number.isFinite(rMin)) { rMin = fc.genRange - 1500; rMax = fc.genRange + 1500; }
    const pad = Math.max(600, (rMax - rMin) * 0.2);
    rMin -= pad; rMax += pad;
    const tx = (t) => x0 + ((t - tMin) / SPAN_T) * w;
    const ry = (r) => y0 + (1 - (r - rMin) / (rMax - rMin)) * h;

    // grid: 1000 m range lines, 30 s time lines
    ctx.strokeStyle = GRID; ctx.lineWidth = 1;
    const rStep = rMax - rMin > 9000 ? 2000 : 1000;
    for (let r = Math.ceil(rMin / rStep) * rStep; r < rMax; r += rStep) {
      ctx.beginPath(); ctx.moveTo(x0, ry(r)); ctx.lineTo(x0 + w, ry(r)); ctx.stroke();
      ctx.fillStyle = DIM; ctx.textAlign = "left"; ctx.font = "9px monospace";
      ctx.fillText(`${(r / 1000).toFixed(0)}km`, x0 + 2, ry(r) - 2);
    }
    for (let t = Math.ceil(tMin / 30) * 30; t < tMax; t += 30) {
      ctx.beginPath(); ctx.moveTo(tx(t), y0); ctx.lineTo(tx(t), y0 + h); ctx.stroke();
    }

    // generated range: the pen line
    ctx.strokeStyle = GOLD; ctx.lineWidth = 1.6;
    ctx.beginPath();
    let started = false;
    for (const p of fc.history) {
      if (p.t < tMin) continue;
      if (!started) { ctx.moveTo(tx(p.t), ry(p.gen)); started = true; }
      else ctx.lineTo(tx(p.t), ry(p.gen));
    }
    if (started) ctx.lineTo(tx(tMax), ry(fc.genRange));
    else { ctx.moveTo(x0, ry(fc.genRange)); ctx.lineTo(tx(tMax), ry(fc.genRange)); }
    ctx.stroke();

    // observed ranges: radar dots / rangefinder cuts
    ctx.fillStyle = OBS;
    for (const p of fc.history) {
      if (p.t < tMin || p.obs === undefined) continue;
      const d = p.dot || 2;
      ctx.beginPath(); ctx.arc(tx(p.t), ry(p.obs), d, 0, Math.PI * 2); ctx.fill();
    }

    // salvo departures and splash groups
    for (const m of fc.salvoMarks) {
      if (m.t < tMin) continue;
      const x = tx(m.t);
      if (m.kind === "salvo") {
        ctx.strokeStyle = "rgba(255,207,92,0.5)"; ctx.setLineDash([3, 4]);
        ctx.beginPath(); ctx.moveTo(x, y0); ctx.lineTo(x, y0 + h); ctx.stroke();
        ctx.setLineDash([]);
      } else {
        ctx.fillStyle = BAD;
        const y = ry(m.range);
        ctx.beginPath(); ctx.moveTo(x, y - 5); ctx.lineTo(x + 5, y + 4); ctx.lineTo(x - 5, y + 4);
        ctx.closePath(); ctx.fill();
      }
    }

    // legend
    ctx.font = "9px monospace"; ctx.textAlign = "right";
    ctx.fillStyle = GOLD; ctx.fillText("— GENERATED", x0 + w, 18);
    ctx.fillStyle = OBS; ctx.fillText("● OBSERVED", x0 + w - 92, 18);
    ctx.fillStyle = BAD; ctx.fillText("▲ SPLASHES", x0 + w - 178, 18);
    // the operator's read: do the slopes agree?
    const dR = fc._lastObsRange !== null ? fc.genRange - fc._lastObsRange : 0;
    ctx.textAlign = "left"; ctx.font = "10px monospace";
    ctx.fillStyle = Math.abs(dR) > 500 ? BAD : DIM;
    ctx.fillText(`PEN vs DOTS: ${dR > 0 ? "+" : ""}${Math.round(dR)} m ${Math.abs(dR) > 500 ? "— ZERO THE PLOT (X)" : ""}`, x0, H - 14);
  }

  drawInstruments(ctx, fc, s) {
    const x0 = PLOT_W + 12, w = W - PLOT_W - 24;
    ctx.font = "11px monospace"; ctx.textAlign = "left"; ctx.fillStyle = DIM;
    ctx.fillText("RATE CONTROL", x0, 18);
    if (!fc.target) {
      ctx.fillStyle = DIM; ctx.font = "10px monospace";
      ctx.fillText("AWAITING DESIGNATION", x0, 44);
      return;
    }

    // --- estimated target course rose ---
    const cx = x0 + w / 2, cy = 88, R = 46;
    ctx.strokeStyle = "#2c4636"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
    for (let b = 0; b < 360; b += 30) {
      const a = b * Math.PI / 180;
      ctx.strokeStyle = GRID;
      ctx.beginPath();
      ctx.moveTo(cx + Math.sin(a) * (R - 4), cy - Math.cos(a) * (R - 4));
      ctx.lineTo(cx + Math.sin(a) * R, cy - Math.cos(a) * R);
      ctx.stroke();
    }
    ctx.fillStyle = DIM; ctx.font = "8px monospace"; ctx.textAlign = "center";
    ctx.fillText("N", cx, cy - R - 3);
    // line of sight to target (dim spoke)
    const brg = fc.targetBearing();
    ctx.strokeStyle = "#3a5a44"; ctx.setLineDash([2, 3]);
    ctx.beginPath(); ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.sin(brg) * R, cy - Math.cos(brg) * R); ctx.stroke();
    ctx.setLineDash([]);
    // own course tick
    ctx.strokeStyle = INK;
    ctx.beginPath(); ctx.moveTo(cx + Math.sin(s.heading) * (R - 9), cy - Math.cos(s.heading) * (R - 9));
    ctx.lineTo(cx + Math.sin(s.heading) * R, cy - Math.cos(s.heading) * R); ctx.stroke();
    // estimated target course arrow
    const ec = fc.est.course;
    ctx.strokeStyle = GOLD; ctx.lineWidth = 2.2;
    ctx.beginPath(); ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.sin(ec) * (R - 7), cy - Math.cos(ec) * (R - 7)); ctx.stroke();
    ctx.fillStyle = GOLD;
    const ax = cx + Math.sin(ec) * (R - 7), ay = cy - Math.cos(ec) * (R - 7);
    ctx.beginPath();
    ctx.arc(ax, ay, 3.4, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = 1;
    ctx.font = "10px monospace"; ctx.textAlign = "center";
    ctx.fillText(`TGT CSE ${deg3(ec)}  SPD ${(fc.est.speed / KTS).toFixed(0)} KTS`, cx, cy + R + 15);

    // --- range rate meter: generated bug vs observed bug ---
    meter(ctx, x0, 168, w, "RANGE RATE (KTS)",
      fc.obsRangeRate / KTS, fc.genRangeRate / KTS, 40);
    // --- bearing drift meter ---
    meter(ctx, x0, 216, w, "BEARING DRIFT (MILS/S)",
      fc.obsBearingRate * 1000, fc.genBearingRate * 1000, 8);

    // --- solution lamp ---
    const q = fc.quality;
    const lampY = 262;
    ctx.fillStyle = q >= 0.8 ? "#7dd87d" : q >= 0.5 ? "#ffb14e" : "#5c3532";
    ctx.beginPath(); ctx.arc(x0 + 12, lampY + 6, 7, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = INK; ctx.font = "11px monospace"; ctx.textAlign = "left";
    ctx.fillText(q >= 0.8 ? "SOLUTION" : q >= 0.5 ? "TRACKING — RELEASED" : "NO SOLUTION", x0 + 26, lampY + 10);
    ctx.fillStyle = DIM; ctx.font = "10px monospace";
    ctx.fillText(`QUALITY ${Math.round(q * 100)}%`, x0 + 26, lampY + 24);
    // quality bar
    ctx.strokeStyle = "#2c4636"; ctx.strokeRect(x0, lampY + 32, w - 4, 8);
    ctx.fillStyle = q >= 0.8 ? "#7dd87d" : GOLD;
    ctx.fillRect(x0 + 1, lampY + 33, (w - 6) * q, 6);

    ctx.fillStyle = DIM; ctx.font = "9px monospace";
    ctx.fillText("MATCH THE GOLD BUGS TO THE GREEN", x0, lampY + 56);
    ctx.fillText("A/D COURSE · W/S SPEED · X ZERO", x0, lampY + 68);
  }

  makeScreenMesh(width = 4.6, height = 2.45) {
    const mat = new THREE.MeshBasicMaterial({ map: this.tex });
    return new THREE.Mesh(new THREE.PlaneGeometry(width, height), mat);
  }
}

// A horizontal needle-matching meter: green = observed, gold = generated.
// The operator's job is to drive the gold bug onto the green one.
function meter(ctx, x, y, w, label, obsVal, genVal, span) {
  ctx.fillStyle = DIM; ctx.font = "9px monospace"; ctx.textAlign = "left";
  ctx.fillText(label, x, y);
  const bw = w - 4, by = y + 6, bh = 14;
  ctx.strokeStyle = "#2c4636"; ctx.strokeRect(x, by, bw, bh);
  ctx.strokeStyle = GRID;
  ctx.beginPath(); ctx.moveTo(x + bw / 2, by); ctx.lineTo(x + bw / 2, by + bh); ctx.stroke();
  const px = (v) => x + Math.max(0.02, Math.min(0.98, 0.5 + v / (2 * span))) * bw;
  // observed (green, lower half)
  ctx.fillStyle = OBS;
  ctx.beginPath();
  ctx.moveTo(px(obsVal), by + bh - 1); ctx.lineTo(px(obsVal) - 4, by + bh + 6); ctx.lineTo(px(obsVal) + 4, by + bh + 6);
  ctx.closePath(); ctx.fill();
  // generated (gold, upper half)
  ctx.fillStyle = GOLD;
  ctx.beginPath();
  ctx.moveTo(px(genVal), by + 1); ctx.lineTo(px(genVal) - 4, by - 6); ctx.lineTo(px(genVal) + 4, by - 6);
  ctx.closePath(); ctx.fill();
  // agreement lamp
  const ok = Math.abs(genVal - obsVal) < span * 0.06;
  ctx.fillStyle = ok ? "#7dd87d" : "#5c3532";
  ctx.beginPath(); ctx.arc(x + bw + 1, by + bh / 2, 3, 0, Math.PI * 2); ctx.fill();
}

function deg3(r) {
  return String(Math.round((((r * 180 / Math.PI) % 360) + 360) % 360)).padStart(3, "0");
}
